import { type AppListItem } from '@app/api/apps';
import useSelector from '@app/hooks/useSelector';
import { getTaskCountFromTasks } from '@app/selectors';
import type { RootState } from '@app/types';

import type { NavigationItem } from '../../../../types/navigation';
import { ProjectType } from '../../../../types/project';

const getTaskCount = (
    project: RootState['project'],
    branch: RootState['branch'],
    taskBadges: RootState['badges']['tasks'],
) => getTaskCountFromTasks(taskBadges || undefined, project.id, branch.branchId || undefined);

// getFeatureState() must be used outside the selector function
const shouldShowFilesTab = (features: RootState['features']): boolean => {
    const feature = features.lionLfcFilesManagement;

    return feature !== null && [true, 'on', 'true'].includes(feature);
};

const getProjectNavigationMap = (
    { project, userRights, branch, badges, features }: RootState,
    apps: AppListItem[],
): NavigationItem[] => {
    const taskCount = getTaskCount(project, branch, badges.tasks);
    const showFilesTab = shouldShowFilesTab(features);

    const contentIntegrationProject = project.type === ProjectType.contentIntegration;

    let contentInputTabs: NavigationItem[] = [
        {
            key: 'upload',
            label: 'Upload',
            link: `/upload/${project.id}/`,
            isActive: (input) => input === 'upload',
            visible: userRights.isAdmin,
            disabled: !project.projectRights.upload,
        },
        {
            key: 'download',
            label: 'Download',
            link: `/download/${project.id}/`,
            isActive: (input) => input === 'download',
            visible: userRights.isAdmin,
            disabled: !project.projectRights.download,
        },
    ];

    const singleInstalledApp = apps.find(({ isInstalled, isSingleInstallApp }) => isInstalled && isSingleInstallApp);

    const appsNavigationItem = {
        key: 'apps',
        label: 'Apps',
        isActive: (input: string) => input === 'apps',
        visible: true,
        link: `/apps/${project.id}/`,
    };

    const appsNavigationItemArrInMainMenu = [];
    const appsNavigationItemArrInMoreMenu = [];

    if (contentIntegrationProject) {
        appsNavigationItemArrInMoreMenu.push(appsNavigationItem);

        contentInputTabs = [
            {
                key: 'contentManagement',
                label: 'Content management',
                isActive: (input: string) => input === 'apps',
                visible: true,
                link: `/apps/${project.id}/${singleInstalledApp?.slug || ''}`,
            },
        ];
    } else {
        appsNavigationItemArrInMainMenu.push(appsNavigationItem);
    }

    return [
        {
            key: 'edit',
            label: 'Editor',
            link: `/project/${project.id}/`,
            isActive: (input: string) => input === 'edit',
            visible: true,
        },
        {
            key: 'files',
            label: 'Files',
            link: `/files/${project.id}/`,
            isActive: (input: string) => input === 'files',
            visible: userRights.isAdmin && +project.type === +ProjectType.pagedDocuments && showFilesTab,
            disabled: !project.projectRights.upload && !project.projectRights.download,
        },
        ...contentInputTabs,
        {
            key: 'tasks',
            label: 'Tasks',
            link: `/tasks/${project.id}/`,
            isActive: (input: string) => input === 'tasks',
            visible: userRights.isAdmin,
            disabled: !project.projectRights.tasks,
            badge: taskCount,
        },
        {
            key: 'contributors',
            label: 'Contributors',
            link: `/contributors/${project.id}/`,
            isActive: (input: string) => input === 'contributors',
            visible: userRights.isAdmin,
            disabled: !project.projectRights.contributors,
        },
        {
            key: 'screenshots',
            label: 'Screenshots',
            link: `/screenshots/${project.id}/`,
            isActive: (input: string) => input === 'screenshots',
            visible: userRights.isAdmin,
            disabled: !project.projectRights.screenshots,
        },
        {
            key: 'glossary',
            label: 'Glossary',
            link: `/glossary/${project.id}/`,
            isActive: (input: string) => input === 'glossary',
            visible: true,
        },
        ...appsNavigationItemArrInMainMenu,
        {
            key: 'more',
            label: 'More...',
            isActive: (input: string) => ['settings', 'statistics', 'duplicates', 'live edit'].includes(input),
            visible: userRights.isAdmin || project.offlineTranslation,
            dropdown: [
                [
                    {
                        link: `/settings/${project.id}/`,
                        label: 'Settings',
                        visible: userRights.isAdmin && project.projectRights.settings,
                    },
                    {
                        link: `/settings/${project.id}/#branches`,
                        label: 'Branches',
                        visible: userRights.isAdmin && project.projectRights.settings && project.isBranchingEnabled,
                    },
                    {
                        link: `/settings/${project.id}/#project-automations`,
                        label: 'Automations',
                        visible: userRights.isAdmin && project.projectRights?.settings,
                    },
                    {
                        link: `/dupes/${project.id}/`,
                        label: 'Duplicate finder',
                        visible:
                            userRights.isAdmin &&
                            project.projectRights.manageKeys &&
                            project.type === ProjectType.localizationFiles,
                    },
                ],
                [
                    ...appsNavigationItemArrInMoreMenu,
                    {
                        label: 'Activity',
                        visible: project.offlineTranslation || project.projectRights.activity,
                        onClick: () => {
                            const showActivityEvent = new CustomEvent('show-activity', {
                                detail: {
                                    projectId: project.id,
                                    branchId: branch.branchId,
                                    branchName: branch.branchName,
                                },
                            });
                            document.dispatchEvent(showActivityEvent);
                        },
                    },
                    {
                        link: `/statistics/${project.id}/`,
                        label: 'Statistics',
                        visible: userRights.isAdmin && project.projectRights.statistics,
                    },
                ],
            ],
        },
    ];
};

const useProjectNavigation = ({ apps }: { apps: AppListItem[] }) => {
    const rootState = useSelector((state) => state);

    return getProjectNavigationMap(rootState, apps);
};

export default useProjectNavigation;
